import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { CartProvider } from './contexts/CartContext'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import InstallPrompt from './components/InstallPrompt'
import Home from './pages/Home'
import Products from './pages/Products'
import Categories from './pages/Categories'
import ProductDetail from './pages/ProductDetail'
import Cart from './pages/Cart'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Profile from './pages/Profile'
import Checkout from './pages/Checkout'
import PaymentSuccess from './pages/PaymentSuccess'
import OrderStatus from './pages/OrderStatus'
import Admin from './pages/AdminSimple'
import AdminTest from './pages/AdminTest'
import AdminDashboard from './pages/AdminDashboard'
import AdminOrderDetails from './pages/AdminOrderDetails'
import AdminInvoicePage from './pages/AdminInvoicePage'
import AdminProductThemes from './pages/AdminProductThemes'

// Force remount of AdminOrderDetails when order ID changes
const AdminOrderDetailsRoute = () => {
  const { id } = useParams()
  return <AdminOrderDetails key={id} />
}
import ProductForm from './pages/ProductForm'
import CategoryForm from './pages/CategoryForm'
import UserForm from './pages/UserForm'
import PromoCodeForm from './pages/PromoCodeForm'
import GoogleCallback from './pages/GoogleCallback'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import ReturnPolicy from './pages/ReturnPolicy'
// Removed complex attribute components - using simplified inline management
import NotFound from './pages/NotFound'
import VerifyCode from './pages/VerifyCode'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

// Component to redirect incorrect user routes
const UserRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/admin/users/edit/${id}`} replace />
}

function AppContent() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="pt-16 flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/category/:slug" element={<Products />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-code" element={<VerifyCode />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentSuccess />} />
          <Route path="/order-status/:orderNumber" element={<OrderStatus />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin-test" element={<AdminTest />} />
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
          <Route path="/admin/orders/:id" element={<AdminOrderDetailsRoute />} />
          <Route path="/admin/orders/:id/invoice" element={<AdminInvoicePage />} />
          <Route path="/admin/product-themes" element={<AdminProductThemes />} />
          <Route path="/admin/products/new" element={<ProductForm />} />
          <Route path="/admin/products/edit/:id" element={<ProductForm />} />
          <Route path="/admin/categories/new" element={<CategoryForm />} />
          <Route path="/admin/categories/edit/:id" element={<CategoryForm />} />
          <Route path="/admin/users/new" element={<UserForm />} />
          <Route path="/admin/users/edit/:id" element={<UserForm />} />
          <Route path="/admin/promocode/new" element={<PromoCodeForm />} />
          <Route path="/admin/promocode/edit/:id" element={<PromoCodeForm />} />
          {/* Simplified: No separate pages for attributes - managed inline */}
          {/* Redirect incorrect user routes to correct ones */}
          <Route path="/admin/user/:id" element={<UserRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            marginTop: '64px', // Position under the fixed navbar (h-16 = 64px)
          },
        }}
      />
      
    </div>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <Router 
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <AppContent />
            <InstallPrompt />
          </Router>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
